import { Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UiModalComponent } from '../../../../shared/components/ui-modal/ui-modal.component';
import { UiSpinnerComponent } from '../../../../shared/components/ui-spinner/ui-spinner.component';
import { Nouveaute, NouveauteFormData, NouveauteSectionFormData } from '../../nouveaute.model';
import {NouveautesService} from '../../nouveautes.service';

@Component({
  selector: 'app-nouveaute-form-modal',
  imports: [UiModalComponent, TranslateModule, UiSpinnerComponent],
  templateUrl: './nouveaute-form-modal.component.html',
  styleUrl: './nouveaute-form-modal.component.scss',
})
export class NouveauteFormModalComponent {
  editData = input<Nouveaute | null>(null);
  isEditMode = computed(() => this.editData() !== null);

  closed = output<void>();
  submitted = output<NouveauteFormData>();
  deleted = output<string>();

  readonly today = new Date().toISOString().split('T')[0];

  submitAttempted = signal(false);

  errors = computed(() => {
    const data = this.formData();
    return {
      title: data.title.trim() ? null : 'REQUIRED',
      publicationDate: this.validateDate(data.publicationDate),
    };
  });

  isFormValid = computed(() => Object.values(this.errors()).every((e) => e === null));

  private validateDate(date: string): string | null {
    if (!date) return 'REQUIRED';
    if (this.isEditMode()) return null;
    if (date < this.today) return 'DATE_PAST';
    return null;
  }

  formData = signal<NouveauteFormData>({
    title: 'Mise à jour Stef Connect',
    publicationDate: '',
    version: '',
    status: 'draft',
    content: '',
    sections: [{ sectionTitle: '', markdownContent: '', imageFile: null }],
  });

  sectionImages = signal<(string | null)[]>([null]);

  private toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  constructor(
    private nouveautesService: NouveautesService
  ) {
    effect(
      () => {
        const data = this.editData();
        if (data) {

          const sections: NouveauteSectionFormData[] =
            data.sections.length > 0
              ? data.sections.map((s) => ({ ...s, imageFile: null }))
              : [{ sectionTitle: '', markdownContent: '', imageFile: null }];

          this.formData.set({
            title: data.title,
            publicationDate: new Date(data.publicationDate * 1000).toISOString().split('T')[0],
            version: data.version,
            status: data.status,
            content: '',
            sections,
          });

          this.sectionImages.set(sections.map(section => null));
          sections.forEach((section: NouveauteSectionFormData, index) => {
            if (section.imageKey) {
              this.nouveautesService.getNouveauteImageURL(section.imageKey).subscribe({
                next: (url: string) => {
                  this.sectionImages.update((sectionImages) => {
                    const updatedSectionImages = [...sectionImages];
                    updatedSectionImages[index] = url;
                    return updatedSectionImages;
                  })
                }
              })
            }
          })

          this.submitAttempted.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  updateField<K extends keyof NouveauteFormData>(field: K, value: NouveauteFormData[K]): void {
    this.formData.update((data) => ({ ...data, [field]: value }));
  }

  toggleStatus(): void {
    this.formData.update((data) => ({
      ...data,
      status: data.status === 'draft' ? 'published' : 'draft',
    }));
  }

  updateSection(
    index: number,
    field: keyof Pick<NouveauteSectionFormData, 'sectionTitle' | 'markdownContent' | 'imageKey'>,
    value: string,
  ): void {
    this.formData.update((data) => {
      const sections = [...data.sections];
      sections[index] = { ...sections[index], [field]: value };
      return { ...data, sections };
    });
  }

  updateSectionImage(index: number, file: File | null): void {
    this.formData.update((data) => {
      const sections = [...data.sections];
      sections[index] = { ...sections[index], imageFile: file, imageKey: undefined };
      return { ...data, sections };
    });

    if (file) {
      this.toBase64(file).then((base64) => {
        this.sectionImages.update((images) => {
          const updated = [...images];
          updated[index] = base64;
          return updated;
        });
      });
    } else {
      this.sectionImages.update((images) => {
        const updated = [...images];
        updated[index] = null;
        return updated;
      });
    }
  }

  addSection(): void {
    this.formData.update((data) => ({
      ...data,
      sections: [...data.sections, { sectionTitle: '', markdownContent: '', imageFile: null }],
    }));

    this.sectionImages.update(images => [
      ...images,
      null
    ]);
  }

  removeSection(index: number): void {
    this.formData.update((data) => ({
      ...data,
      sections: data.sections.filter((_, i) => i !== index),
    }));

    this.sectionImages.update(images =>
      images.filter((_, i) =>
        i !== index
      )
    );
  }

  applyMarkdownFormat(
    textarea: HTMLTextAreaElement,
    index: number,
    format: 'bold' | 'italic' | 'underline' | 'link',
  ): void {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.substring(start, end);
    const current = this.formData().sections[index].markdownContent;

    let before = '';
    let after = '';
    let placeholder = selected;

    switch (format) {
      case 'bold':
        before = '**';
        after = '**';
        placeholder = selected || 'texte en gras';
        break;
      case 'italic':
        before = '*';
        after = '*';
        placeholder = selected || 'texte en italique';
        break;
      case 'underline':
        before = '<u>';
        after = '</u>';
        placeholder = selected || 'texte souligné';
        break;
      case 'link':
        before = '[';
        after = '](https://)';
        placeholder = selected || 'texte du lien';
        break;
    }

    const newValue =
      current.substring(0, start) + before + placeholder + after + current.substring(end);
    this.updateSection(index, 'markdownContent', newValue);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + placeholder.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  }

  onImageChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.updateSectionImage(index, file);
  }

  onRemoveImage(index: number): void {
    this.updateSectionImage(index, null);
    this.sectionImages.update(images => {
      const updated = [...images];
      updated[index] = null;
      return updated;
    });
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isFormValid()) return;
    this.submitted.emit(this.formData());
    this.onClose();
  }

  onDelete(): void {
    const data = this.editData();
    if (data?._id) {
      this.deleted.emit(data._id);
      this.onClose();
    }
  }

  onClose(): void {
    this.formData.set({
      title: 'Mise à jour Stef Connect',
      publicationDate: '',
      version: '',
      status: 'draft',
      content: '',
      sections: [{ sectionTitle: '', markdownContent: '', imageFile: null }],
    });
    this.sectionImages.set([null]);
    this.submitAttempted.set(false);
    this.closed.emit();
  }
}
